import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useClickTracking } from '../contexts/ClickTrackingContext';
import { FileText, FolderOpen, BookOpen, CreditCard, DollarSign, Video, Send, Mail, AlertCircle, Gavel, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useNavigationConfig from '../hooks/useNavigationConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function useMemberDashboard() {
  const { t: t } = useTranslation();
  const { user, logout, getAuthHeader } = useAuth();
  const { trackClick } = useClickTracking();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [member, setMember] = useState(null);
  const [souscriptions, setSouscriptions] = useState([]);
  const [dommages, setDommages] = useState([]);
  const [loading, setLoading] = useState(true);
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'souscriptions');
  const [showDeclarationForm, setShowDeclarationForm] = useState(false);

  // Sync URL ?tab=… → activeTab
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line
  }, [tabFromUrl]);

  // Mettre à jour l'URL quand l'onglet change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', newTab);
    setSearchParams(next, { replace: true });
  };

  // Tracker les clics sur les actions principales
  const _handleTrackedClick = (callback) => {
    trackClick();
    if (callback) callback();
  };

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const [memberRes, souscriptionsRes, dommagesRes] = await Promise.all([
        axios.get(`${API}/members/me`, { headers: getAuthHeader() }).catch((_err) => {
          return { data: null };
        }),
        axios.get(`${API}/souscriptions/me`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/dommages/me`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      
      // Si pas de profil membre, utiliser les données de l'utilisateur connecté
      setMember(memberRes.data || { 
        prenom: user?.prenom || '', 
        nom: user?.nom || '',
        numero_membre: 'En cours de création'
      });
      
      // Trier les souscriptions du plus récent au plus ancien
      const sortedSouscriptions = (souscriptionsRes.data || []).sort((a, b) => {
        const dateA = new Date(a.date_creation || a.created_at || 0);
        const dateB = new Date(b.date_creation || b.created_at || 0);
        return dateB - dateA; // Plus récent en premier
      });
      setSouscriptions(sortedSouscriptions);

      setDommages(dommagesRes.data);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const _handleDownloadAttestation = async (souscriptionId = null) => {
    try {
      if (!souscriptionId) {
        toast.error('ID de souscription manquant');
        return;
      }
      
      // Afficher un toast de chargement
      const toastId = toast.loading('Génération du document PDF en cours...');
      
      // Utiliser l'endpoint PDF rcds_unique avec timestamp anti-cache
      const timestamp = Date.now();
      const url = `${API}/souscriptions-rcd/${souscriptionId}/documents/rcds_unique?t=${timestamp}`;
      
      
      // Fetch the PDF content with no-cache headers
      const response = await axios.get(url, {
        headers: {
          ...getAuthHeader(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        responseType: 'blob',
        timeout: 60000 // 60 secondes timeout
      });
      
      
      // Fermer le toast de chargement
      toast.dismiss(toastId);

      if (response.data && response.data.size > 0) {
        // Create a Blob from the PDF content
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        
        // Télécharger directement le PDF (plus fiable que window.open)
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `Document_RCDS_Complet_${souscriptionId}.pdf`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Nettoyer après un délai
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
        
        toast.success(`Document RCDS téléchargé ! (${(blob.size / 1024 / 1024).toFixed(1)} MB)`);
      } else {
        toast.error('Le document PDF est vide ou invalide');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement du document PDF');
    }
  };

  // Fonction pour consulter le document (lecture seule, sans téléchargement)
  const _handleViewDocument = async (souscriptionId) => {
    try {
      if (!souscriptionId) {
        toast.error('ID de souscription manquant');
        return;
      }
      
      const toastId = toast.loading('Chargement du document...');
      
      const timestamp = Date.now();
      const url = `${API}/souscriptions-rcd/${souscriptionId}/documents/rcds_unique?t=${timestamp}`;
      
      const response = await axios.get(url, {
        headers: {
          ...getAuthHeader(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        responseType: 'blob',
        timeout: 60000
      });
      
      toast.dismiss(toastId);

      if (response.data && response.data.size > 0) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        // Ouvrir dans une nouvelle fenêtre en mode lecture seule
        // Utiliser les paramètres PDF pour désactiver impression et téléchargement
        const viewerUrl = `${blobUrl}#toolbar=0&navpanes=0&scrollbar=1`;
        
        const viewerWindow = window.open('', '_blank', 'width=900,height=700,menubar=no,toolbar=no,location=no,status=no');
        
        if (viewerWindow) {
          viewerWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Document RCDS - Consultation</title>
              <style>
                * { margin: 0; padding: 0; }
                body { 
                  background: #333; 
                  overflow: hidden;
                  -webkit-user-select: none;
                  -moz-user-select: none;
                  -ms-user-select: none;
                  user-select: none;
                }
                .header {
                  background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
                  color: white;
                  padding: 12px 20px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  font-family: Arial, sans-serif;
                }
                .header h1 { font-size: 16px; font-weight: 600; }
                .header span { font-size: 12px; opacity: 0.8; }
                iframe { 
                  width: 100%; 
                  height: calc(100vh - 50px); 
                  border: none;
                }
                @media print { body { display: none !important; } }
              </style>
              <script>
                // Désactiver clic droit
                document.addEventListener('contextmenu', e => e.preventDefault());
                // Désactiver raccourcis clavier
                document.addEventListener('keydown', e => {
                  if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's' || e.key === 'P' || e.key === 'S')) {
                    e.preventDefault();
                    alert('Impression et téléchargement désactivés en mode consultation.\\nUtilisez le bouton Télécharger depuis votre espace membre.');
                  }
                });
              </script>
            </head>
            <body>
              <div class="header">
                <h1>📄 Document RCDS - Mode Consultation</h1>
                <span>Lecture seule - Téléchargement depuis l'espace membre</span>
              </div>
              <iframe src="${viewerUrl}" title="Document RCDS"></iframe>
            </body>
            </html>
          `);
          viewerWindow.document.close();
          toast.success('Document ouvert en mode consultation');
        } else {
          toast.error('Popup bloqué - Autorisez les popups pour consulter le document');
        }
        
        // Nettoyer après 5 minutes
        setTimeout(() => URL.revokeObjectURL(blobUrl), 300000);
      } else {
        toast.error('Le document est vide ou invalide');
      }
    } catch {
      toast.error('Erreur lors de la consultation du document');
    }
  };

  const _handleDownloadConditionsGenerales = async () => {
    try {
      const response = await axios.get(`${API}/conditions-generales/html/membre`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conditions_generales_RCDS_BATICO_${member?.numero_membre || 'membre'}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Conditions générales RCDS BATICO téléchargées avec succès !');
    } catch {
      toast.error('Erreur lors du téléchargement des conditions générales');
    }
  };

  const _handleSignerConditionsGenerales = async () => {
    try {
      const response = await axios.post(`${API}/conditions-generales/signer`, {}, {
        headers: getAuthHeader()
      });

      toast.success('✅ Conditions générales signées électroniquement avec horodatage !');
      
      // Afficher les détails de la signature
      setTimeout(() => {
        toast.info(`🔒 Signature ID: ${response.data.signature_id.substring(0, 16)}...`, {
          duration: 5000
        });
      }, 1000);

      // Recharger les données pour mettre à jour l'affichage
      setTimeout(() => {
        fetchMemberData();
      }, 2000);
    } catch {
      toast.error('Erreur lors de la signature des conditions générales');
    }
  };

  const _handleDownloadProposition = async (souscriptionId = null) => {
    try {
      const url = souscriptionId 
        ? `${API}/proposition/html/membre?souscription_id=${souscriptionId}`
        : `${API}/proposition/html/membre`;
      
      const response = await axios.get(url, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });

      if (response.data) {
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `proposition_RCDS_BATICO_${member?.numero_membre || 'membre'}.html`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        toast.success('Proposition RCDS BATICO téléchargée avec succès !');
      } else {
        toast.error('Le document de proposition est vide');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement de la proposition');
    }
  };

  const _handleSignerProposition = async (souscriptionId = null) => {
    try {
      const body = souscriptionId ? { souscription_id: souscriptionId } : {};
      const response = await axios.post(`${API}/proposition/signer${souscriptionId ? `?souscription_id=${souscriptionId}` : ''}`, body, {
        headers: getAuthHeader()
      });

      if (response.data && response.data.success) {
        toast.success('✅ Proposition RCDS BATICO signée électroniquement avec horodatage !');
        
        // Afficher les détails de la signature si disponible
        if (response.data.signature_id) {
          setTimeout(() => {
            toast.info(`🔒 Signature ID: ${response.data.signature_id.substring(0, 16)}...`, {
              duration: 5000
            });
          }, 1000);
        }

        // Recharger les données pour mettre à jour l'affichage
        setTimeout(() => {
          fetchMemberData();
        }, 2000);
      } else {
        toast.error('Erreur lors de la signature de la proposition');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature de la proposition');
    }
  };

  const _handleDownloadConditionsParticulieres = async (souscriptionId = null) => {
    try {
      const url = souscriptionId 
        ? `${API}/conditions-particulieres/html/membre?souscription_id=${souscriptionId}`
        : `${API}/conditions-particulieres/html/membre`;
      
      const response = await axios.get(url, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });

      if (response.data) {
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `conditions_particulieres_RCDS_${member?.numero_membre || 'membre'}.html`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        toast.success('Conditions Particulières téléchargées avec succès !');
      } else {
        toast.error('Le document des conditions particulières est vide');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement des conditions particulières');
    }
  };

  const _handleSignerConditionsParticulieres = async () => {
    try {
      const response = await axios.post(`${API}/conditions-particulieres/signer`, {}, {
        headers: getAuthHeader()
      });

      if (response.data && response.data.success) {
        toast.success('✅ Conditions Particulières signées électroniquement !');
        
        if (response.data.signature_id) {
          setTimeout(() => {
            toast.info(`🔒 Signature ID: ${response.data.signature_id.substring(0, 16)}...`, {
              duration: 5000
            });
          }, 1000);
        }

        setTimeout(() => {
          fetchMemberData();
        }, 2000);
      } else {
        toast.error('Erreur lors de la signature des conditions particulières');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature des conditions particulières');
    }
  };

  // Télécharger le reçu ESS (contribution solidaire)
  const _handleDownloadRecuESS = async (souscription) => {
    try {
      // Récupérer le reçu ESS via l'ID stocké dans la souscription
      const recuId = souscription.recu_ess_id;
      if (!recuId) {
        toast.error('Aucun reçu ESS disponible pour cette souscription');
        return;
      }

      toast.loading('Téléchargement en cours...', { id: 'download-recu' });

      const response = await axios.get(
        `${API}/recus-ess/${encodeURIComponent(recuId)}/download`,
        {
          headers: getAuthHeader(),
          responseType: 'blob',
          timeout: 30000
        }
      );

      // Vérifier que la réponse est bien un PDF
      if (!response.data || response.data.size === 0) {
        toast.dismiss('download-recu');
        toast.error('Le fichier reçu est vide ou invalide');
        return;
      }

      // Vérifier le type de contenu
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // C'est une erreur JSON, pas un PDF
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          toast.dismiss('download-recu');
          toast.error(errorData.detail || 'Erreur lors du téléchargement');
          return;
        } catch {
          // Ignorer l'erreur de parsing
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recu_ESS_${souscription.recu_ess_numero || souscription.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss('download-recu');
      toast.success('Reçu ESS téléchargé avec succès !');
    } catch (error) {
      toast.dismiss('download-recu');
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Délai d\'attente dépassé. Veuillez réessayer.');
      } else if (error.response?.status === 404) {
        toast.error('Reçu ESS non trouvé. Veuillez contacter l\'administrateur.');
      } else if (error.response?.status === 403) {
        toast.error('Accès non autorisé à ce reçu.');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement du reçu ESS');
      }
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const sidebarTabs = [
    { value: 'souscriptions', label: 'Mes Dispositifs', icon: <FileText className="h-5 w-5" /> },
    { value: 'mes-documents', label: 'Mes Documents', icon: <FolderOpen className="h-5 w-5 text-blue-600" />, highlight: true },
    { value: 'formations', label: 'Mes Formations', icon: <BookOpen className="h-5 w-5" /> },
    { value: 'wallet', label: 'Mon Wallet', icon: <CreditCard className="h-5 w-5" /> },
    { value: 'abonnement', label: 'Abonnement', icon: <DollarSign className="h-5 w-5" /> },
    { value: 'documents', label: 'Documents', icon: <FolderOpen className="h-5 w-5" /> },
    { value: 'videos', label: 'Photos/Vidéos', icon: <Video className="h-5 w-5" /> },
    { value: 'mes-partages', label: 'Mes Partages', icon: <Send className="h-5 w-5 text-indigo-600" /> },
    { value: 'messagerie', label: 'Messagerie', icon: <Mail className="h-5 w-5" /> },
    { value: 'dommages', label: 'Mes Dommages', icon: <AlertCircle className="h-5 w-5" /> },
    { value: 'pv-chantiers', label: 'Procès-Verbaux', icon: <Gavel className="h-5 w-5" /> },
    { value: 'bibliotheque', label: 'Bibliothèque', icon: <BookOpen className="h-5 w-5" /> },
    { value: 'profil', label: 'Mon Profil', icon: <User className="h-5 w-5" /> },
  ];

  // V36.3 — Masquage des onglets selon configuration Super Admin (rôle + override user)
  const { isHidden: isNavHidden } = useNavigationConfig();
  const visibleSidebarTabs = sidebarTabs.filter((t) => !isNavHidden(t.value));

  return {
    _handleDownloadAttestation, _handleDownloadConditionsGenerales, _handleDownloadConditionsParticulieres, _handleDownloadProposition, _handleDownloadRecuESS, _handleSignerConditionsGenerales,
    _handleSignerConditionsParticulieres, _handleSignerProposition, _handleTrackedClick, _handleViewDocument, activeTab, dommages,
    fetchMemberData, getAuthHeader, handleLogout, handleTabChange, loading, logout,
    member, navigate, searchParams, setActiveTab, setDommages, setLoading,
    setMember, setSearchParams, setShowDeclarationForm, setSouscriptions, showDeclarationForm, sidebarTabs,
    souscriptions, t, tabFromUrl, trackClick, user, visibleSidebarTabs,
  };
}
